/* eslint-disable max-len */
import { useField, useFormikContext } from 'formik'
import React from 'react'
import { useDispatch } from 'react-redux'
import { Form } from 'react-bootstrap'
import styled from 'styled-components'
import { FormGroup } from 'components'
import { _, colors, resizeImage } from 'utils'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import { FileUploadProps } from 'features/forms/types'
import { validImageExtensions } from 'features/booth/config/AssetCard/AssetImageCard'
import { showImageLibrary } from 'features/image_library/imageLibrarySlice'
import ImageLibraryModal from 'features/image_library/ImageLibraryModal'
import { addModal } from 'features/modals/modalSlice'
import useImageFromLibrary from 'hooks/useImageFromLibrary'
import ImagePreviewPopover from './ImagePreviewPopover'

export const PDF_PPT_ONLY = 'application/pdf, .ppt, .pptx'
export const IMAGE_ONLY = 'image/*'
export const VIDEOS_ONLY = 'video/mp4'
export const EXCEL_CSV_ONLY = '.xls, .xlsx, .csv'

const S = {
    FileInput: styled.div<{ isDisabled: boolean }>`
        display: flex;
        align-items: center;
        width: 100%;
        height: 44px;
        margin-bottom: -20px;
        padding: 0 16px;
        border: 1px solid ${colors.inputBorder};
        border-radius: 4px;
        background-color: ${({ isDisabled }) => isDisabled && '#efefef'};
    `,
    LabelWrapper: styled.div`
        margin-top: 3.5px;
    `,
    HiddenInput: styled(Form.File)`
        opacity: 0 !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden;
    `,
    Link: styled.a`
        text-decoration: none;
        font-size: 14px;
        line-height: 16px;
        margin-bottom: 8px;
        color: ${colors.primary};
        cursor: pointer;
    `,
    Name: styled.span<{ displayedValue?: string }>`
        flex: 1;
        font-size: 16px;
        font-weight: 300;
        color: ${({ displayedValue }) => (displayedValue ? colors.dark : colors.grayTitleOpacity(0.4))};
    `,
    ActionButton: styled.div`
        height: 100%;
        max-width: 160px;
        margin-left: 10px;
        padding-left: 20px;
        padding-right: 4px;
        border-left: 1px solid ${colors.inputBorder};
        line-height: 42px;
        color: ${colors.grayTitle};
        opacity: 0.9;
        cursor: pointer;
    `,
}

const FileUpload = ({
    name,
    label,
    helperText,
    placeholder = '',
    isRequired,
    accept,
    maxSize = 20,
    dependField,
    isDisabled,
    maxImageSize,
    previewImagePlacement = 'bottom-end',
    hideLibrary,
    showLibraryInModal,
    ...props
}: FileUploadProps) => {
    const [
        field,
        meta,
        helper,
    ] = useField({ name })
    const { setFieldValue } = useFormikContext()
    const { error, touched } = meta
    const dispatch = useDispatch()
    useImageFromLibrary(field.name, helper.setValue)

    const handleChange = async (e) => {
        const file = e.currentTarget?.files[0]
        const extension: string = file?.name.split('.').pop() || ''
        helper.setTouched(true)
        if (file?.size > maxSize * 1024 * 1024) {
            dispatch(showErrorNotification(`Max size of file is ${maxSize} Mbs`, 3000))
            return
        }
        if (accept === PDF_PPT_ONLY) {
            if (!['pdf', 'ppt', 'pptx'].includes(extension.toLowerCase())) {
                dispatch(showErrorNotification('The uploaded file\'s type is not accepted', 3000))
                return
            }
        } else if (accept === IMAGE_ONLY) {
            if (!validImageExtensions.includes(extension.toLowerCase())) {
                dispatch(showErrorNotification('The uploaded file\'s type is not accepted', 3000))
                return
            }
            if (maxImageSize) {
                const resizedImageFile = await resizeImage(file, {
                    maxWidth: maxImageSize.width,
                    maxHeight: maxImageSize.height
                })
                helper.setValue(resizedImageFile)
                return
            }
        } else if (accept === EXCEL_CSV_ONLY) {
            if (!EXCEL_CSV_ONLY.includes(extension.toLowerCase())) {
                dispatch(showErrorNotification('The uploaded file\'s type is not accepted', 3000))
                return
            }
        }
        helper.setValue(e.currentTarget.files[0])
    }

    const handleRemove = (e) => {
        e.stopPropagation()
        setFieldValue(dependField, '')
        helper.setValue('')
    }
    const displayedValue = (typeof field.value === 'string') ? field.value : field.value?.name

    const openImageLibrary = (event) => {
        event.preventDefault()
        if (showLibraryInModal) {
            dispatch(addModal({
                Component: ({ close }: { close: () => void }) => <ImageLibraryModal close={close} field={field.name} />,
                header: 'Image Library',
                size: 'xl',
            }))
            return
        }
        dispatch(showImageLibrary(field.name))
    }

    return (
        <FormGroup
            controlId={`${field.name}-file`}
            {...props}
        >
            <S.LabelWrapper className="flex-space-between">
                <Form.Label style={{ pointerEvents: 'none' }}>
                    {label}
                    {isRequired && <span>&nbsp;*</span>}
                </Form.Label>
                {(accept === PDF_PPT_ONLY) && (typeof field.value === 'string') && field.value && (
                    <S.Link href={field.value} target="_blank">
                        {_('button.preview')}
                    </S.Link>
                )}
                {accept === IMAGE_ONLY && !hideLibrary && (
                    <S.Link href={field.value} onClick={openImageLibrary}>
                        {_('button.choose_from_image_library')}
                    </S.Link>
                )}
            </S.LabelWrapper>
            <S.FileInput isDisabled={isDisabled}>
                <S.Name className="text-cut" displayedValue={displayedValue}>
                    {displayedValue || placeholder}
                </S.Name>
                {
                    !field.value ? (
                        <Form.Label className="mb-0">
                            <S.ActionButton className="text-cut">
                                {_('button.browse')}
                            </S.ActionButton>
                        </Form.Label>
                    ) : (
                        <S.ActionButton onClick={handleRemove} className="text-cut">
                            {_('button.remove')}
                        </S.ActionButton>
                    )
                }
            </S.FileInput>
            <S.HiddenInput
                id={`${field.name}-file`}
                label={placeholder}
                value=""
                name={field.name}
                multiple={field.multiple}
                accept={accept}
                onChange={handleChange}
                custom
            />

            <div className="flex-space-between">
                {!(touched && error) && helperText ? (
                    <Form.Text muted className="mt-0 mb-2">{helperText}</Form.Text>
                ) : <div />}
                {accept === IMAGE_ONLY && field.value && typeof field.value === 'string' && (
                    <ImagePreviewPopover image={field.value} previewImagePlacement={previewImagePlacement} />
                )}
            </div>

            {touched && error && (
                <small className="text-danger invalid-field">
                    {_(error)}
                </small>
            )}
        </FormGroup>
    )
}

export default FileUpload
